// src/Website/Components/ProductPage.jsx
//
// Shared skeleton for the four product marketing pages
// (ATS / HRMS / Jobs / Recruit). Each page becomes a thin config
// passed to <ProductPage />.
//
// Sections (in order):
//   1. Hero, tagline + accent + corner markers + topology
//   2. Feature bento, hairline-grid of benefits
//   3. Pipeline / flow, numbered stages connected by dashed hairlines
//   4. Metrics strip, tabular stats (optional)
//   5. CTA panel, skeuomorphic primary button into the app

import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Rocket, Sparkles, ArrowUpRight } from "lucide-react";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  colors as DS,
  motion as M,
} from "../../design-system";

import HeroVisual from "./HeroAnimations";

const fade = M.variants.fadeUp;

/* ───────────────────────── HERO ───────────────────────── */
function Hero({ meta, hero, ctaPrimary, ctaSecondary }) {
  const reduce = useReducedMotion();
  const Icon = hero.icon;

  return (
    <section
      className="relative pt-16 pb-24 md:pt-24 md:pb-32 border-b ds-bg-spotlight"
      style={{ borderColor: DS.hairline }}
    >
      <PageContainer>
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 xl:gap-20 items-center">
          {/* Copy */}
          <motion.div
            className="lg:col-span-7"
            initial="hidden"
            animate="show"
            variants={fade}
          >
            <motion.div variants={fade} custom={0} className="flex items-stretch gap-4 mb-7">
              <span className="w-px self-stretch" style={{ background: hero.accent }} />
              <div className="flex flex-col gap-1.5">
                <TechLabel style={{ color: hero.accent }}>
                  {hero.subtitle}
                </TechLabel>
                <TechLabel style={{ color: `${DS.grid}99` }}>
                  module · {meta.code} · v2.0
                </TechLabel>
              </div>
            </motion.div>

            <motion.h1
              variants={fade}
              custom={1}
              className="text-5xl sm:text-6xl lg:text-[5.25rem] xl:text-[6rem] font-extrabold leading-[0.92] tracking-tight"
              style={{ color: DS.forest }}
            >
              {hero.title}
            </motion.h1>

            <motion.p
              variants={fade}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: DS.grid }}
            >
              {hero.description}
            </motion.p>

            <motion.div variants={fade} custom={3} className="mt-10 flex flex-wrap items-center gap-3">
              {ctaPrimary && (
                <Link to={ctaPrimary.href}>
                  <button
                    type="button"
                    className="ds-skeu-btn-primary cursor-pointer h-12 px-7 text-sm font-semibold inline-flex items-center"
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    {ctaPrimary.label}
                  </button>
                </Link>
              )}
              {ctaSecondary && (
                <Link to={ctaSecondary.href}>
                  <button
                    type="button"
                    className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center group transition-colors duration-150"
                    style={{ borderColor: DS.forest, color: DS.forest }}
                  >
                    {ctaSecondary.label}
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                  </button>
                </Link>
              )}
            </motion.div>
          </motion.div>

          {/* Visual, Feature topology echoing the home hero */}
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0 : 0.7, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="relative w-full max-w-[480px] mx-auto">
              <div className="relative aspect-square border ds-glass-tile p-6 flex items-center justify-center" style={{ borderColor: DS.hairline }}>
                <CornerMarkers color={hero.accent} />

                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className="w-2 h-2" style={{ background: hero.accent }} />
                  <TechLabel style={{ color: hero.accent }}>
                    {meta.code} · core
                  </TechLabel>
                </div>
                <TechLabel className="absolute top-3 right-3" style={{ color: `${DS.grid}99` }}>
                  04 mods
                </TechLabel>

                {/* Animated product visual, variant chosen per product */}
                <HeroVisual
                  variant={hero.animation}
                  accent={hero.accent}
                  Icon={Icon}
                  terminalLines={hero.terminalLines}
                />

                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <TechLabel style={{ color: `${DS.grid}99` }}>{meta.code} engine</TechLabel>
                  <TechLabel style={{ color: hero.accent }}>● online</TechLabel>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </PageContainer>
    </section>
  );
}

/* ───────────────────────── FEATURE BENTO ───────────────────────── */
function FeatureBento({ index, label, title, subtitle, features, accent }) {
  return (
    <section className="py-20 md:py-24 border-b" style={{ borderColor: DS.hairline }}>
      <PageContainer>
        <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-2xl">
            <StatusBadge color={accent}>
              {index} · {label}
            </StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: DS.forest }}>
              {title}
            </h2>
            {subtitle && (
              <p className="mt-4 text-lg" style={{ color: DS.grid }}>{subtitle}</p>
            )}
          </div>
          <TechLabel style={{ color: `${DS.grid}99` }}>
            {String(features.length).padStart(2, "0")} modules · live
          </TechLabel>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px"
          style={{ background: DS.hairline }}
        >
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="ds-spatial relative p-7 md:p-8 group cursor-pointer transition-colors duration-150 hover:bg-white"
              style={{ background: DS.paper, borderLeft: `4px solid ${accent}` }}
            >
              <div className="flex items-center justify-between mb-5">
                <div
                  className="w-11 h-11 flex items-center justify-center"
                  style={{ background: DS.forest, color: DS.paper }}
                >
                  <f.icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <TechLabel style={{ color: `${DS.grid}99` }}>
                </TechLabel>
              </div>
              <h3 className="text-xl font-bold leading-tight" style={{ color: DS.forest }}>
                {f.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: DS.grid }}>
                {f.description}
              </p>
              <ArrowUpRight
                className="absolute top-6 right-6 w-4 h-4 transition-all duration-150 opacity-0 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                style={{ color: accent }}
              />
            </motion.div>
          ))}
        </div>
      </PageContainer>
    </section>
  );
}

/* ───────────────────────── PIPELINE / FLOW ───────────────────────── */
function PipelineFlow({ index, title, stages, accent }) {
  return (
    <section className="py-20 md:py-24 border-b" style={{ borderColor: DS.hairline }}>
      <PageContainer>
        <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-2xl">
            <StatusBadge color={accent}>
              {index} · Pipeline
            </StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: DS.forest }}>
              {title}
            </h2>
          </div>
          <TechLabel style={{ color: `${DS.grid}99` }}>
            {String(stages.length).padStart(2, "0")} stages
          </TechLabel>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-4 gap-px" style={{ background: DS.hairline }}>
          {stages.map((stage, i) => (
            <motion.div
              key={stage.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="relative p-7 md:p-8"
              style={{ background: DS.paper }}
            >
              <div className="flex items-center justify-between mb-5">
                <span
                  className="w-12 h-12 flex items-center justify-center text-base font-bold border-2"
                  style={{ borderColor: accent, color: accent, fontVariantNumeric: "tabular-nums" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
 
              </div>
              <h3 className="text-lg font-bold leading-tight" style={{ color: DS.forest }}>
                {stage.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: DS.grid }}>
                {stage.desc}
              </p>
              {/* Connector arrow on desktop */}
              {i < stages.length - 1 && (
                <span
                  aria-hidden="true"
                  className="hidden md:block absolute top-1/2 -right-2 w-4 h-px"
                  style={{ background: accent }}
                />
              )}
            </motion.div>
          ))}
        </div>
      </PageContainer>
    </section>
  );
}

/* ───────────────────────── METRICS STRIP ───────────────────────── */
function MetricsStrip({ index, title, stats, accent }) {
  if (!stats?.length) return null;
  return (
    <section className="py-20 md:py-24 border-b" style={{ borderColor: DS.hairline, background: DS.forest, color: DS.paper }}>
      <PageContainer>
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-2xl">
            <StatusBadge color={DS.mint}>
              {index} · Impact
            </StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight">
              {title}
            </h2>
          </div>
          <TechLabel style={{ color: "rgba(247,247,245,0.55)" }}>
            avg · all customers
          </TechLabel>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: "rgba(158,255,191,0.22)" }}>
          {stats.map((s, i) => (
            <div
              key={s.label}
              className="p-6 md:p-8"
              style={{ background: DS.forest }}
            >
              <TechLabel style={{ color: accent }}></TechLabel>
              <div className="mt-2 text-4xl md:text-5xl font-extrabold tracking-tight ds-tabular">
                {s.value}
              </div>
              <p className="mt-1 text-sm" style={{ color: "rgba(247,247,245,0.7)" }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </PageContainer>
    </section>
  );
}

/* ───────────────────────── CTA ───────────────────────── */
function CtaPanel({ title, subtitle, ctaPrimary, ctaSecondary }) {
  return (
    <section className="py-20 md:py-24">
      <PageContainer>
        <div
          className="relative border p-10 md:p-14 text-center"
          style={{ borderColor: DS.hairline, background: DS.paper }}
        >
          <CornerMarkers color={DS.forest} />
          <div className="flex justify-center mb-5">
            <StatusBadge>Get started</StatusBadge>
          </div>
          <h2 className="text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: DS.forest }}>
            {title}
          </h2>
          {subtitle && (
            <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto" style={{ color: DS.grid }}>
              {subtitle}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {ctaPrimary && (
              <Link to={ctaPrimary.href}>
                <button type="button" className="ds-skeu-btn-primary cursor-pointer h-12 px-7 text-sm font-semibold inline-flex items-center">
                  <Sparkles className="w-4 h-4 mr-2" />
                  {ctaPrimary.label}
                </button>
              </Link>
            )}
            {ctaSecondary && (
              <Link to={ctaSecondary.href}>
                <button
                  type="button"
                  className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center group transition-colors duration-150"
                  style={{ borderColor: DS.forest, color: DS.forest }}
                >
                  {ctaSecondary.label}
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                </button>
              </Link>
            )}
          </div>
        </div>
      </PageContainer>
    </section>
  );
}

/* ───────────────────────── COMPOSED PAGE ───────────────────────── */
export default function ProductPage({
  meta,
  hero,
  heroOverride,
  features,
  flow,
  stats,
  ctaPrimary,
  ctaSecondary,
  finalCta,
}) {
  return (
    <div
      className="relative"
      style={{
        background: DS.paper,
        color: DS.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {heroOverride ? (
        heroOverride
      ) : (
        <Hero
          meta={meta}
          hero={hero}
          ctaPrimary={ctaPrimary}
          ctaSecondary={ctaSecondary}
        />
      )}

      <FeatureBento
        label="Capabilities"
        title={features.title}
        subtitle={features.subtitle}
        features={features.items}
        accent={hero.accent}
      />

      {flow?.stages?.length > 0 && (
        <PipelineFlow
          title={flow.title}
          stages={flow.stages}
          accent={hero.accent}
        />
      )}

      {stats?.items?.length > 0 && (
        <MetricsStrip
          title={stats.title}
          stats={stats.items}
          accent={hero.accent === DS.mint ? DS.coral : DS.mint}
        />
      )}

      <CtaPanel
        title={finalCta?.title || "Ready to get started?"}
        subtitle={finalCta?.subtitle}
        ctaPrimary={finalCta?.ctaPrimary || ctaPrimary}
        ctaSecondary={finalCta?.ctaSecondary || ctaSecondary}
      />
    </div>
  );
}
